import { useCallback, useEffect, useMemo, useState } from "react";

import { useFetchPrintCatalogQuery } from "../../../api/printingApi";

export const useDebouncedValue = (value, delay = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

/**
 * Pages of the printing catalog for the current filters, accumulated for
 * "Load more". Changing filters starts again at page 1; pages from different
 * filters are never mixed. Selection lives elsewhere, so nothing here can lose it.
 */
const usePrintCatalog = (params, { skip = false } = {}) => {
  const filterKey = JSON.stringify(params);
  const [cursor, setCursor] = useState({ key: filterKey, page: 1 });
  if (cursor.key !== filterKey) setCursor({ key: filterKey, page: 1 });
  const page = cursor.key === filterKey ? cursor.page : 1;

  const { currentData, isFetching, isError, refetch } = useFetchPrintCatalogQuery(
    { ...params, page, pageSize: 30 },
    { skip }
  );

  const [loaded, setLoaded] = useState({ key: filterKey, pages: {}, meta: null });
  useEffect(() => {
    if (!currentData) return;
    setLoaded((previous) => ({
      key: filterKey,
      pages: { ...(previous.key === filterKey ? previous.pages : {}), [currentData.page]: currentData.items || [] },
      meta: currentData,
    }));
  }, [currentData, filterKey]);

  const isStale = loaded.key !== filterKey;
  const products = useMemo(() => {
    const seen = new Set();
    return Object.keys(loaded.pages)
      .map(Number)
      .sort((a, b) => a - b)
      .flatMap((number) => loaded.pages[number])
      .filter((product) => (seen.has(product.id) ? false : seen.add(product.id)));
  }, [loaded.pages]);

  const meta = loaded.meta;
  const hasMore = Boolean(meta) && !isStale && page < (meta.totalPages || 1);
  const loadMore = useCallback(() => {
    if (isFetching || !hasMore) return;
    setCursor({ key: filterKey, page: page + 1 });
  }, [filterKey, hasMore, isFetching, page]);

  return {
    products,
    total: meta?.total ?? 0,
    hasMore,
    loadMore,
    isInitialLoading: !meta && !isError,
    isRefreshing: Boolean(meta) && (isStale || (isFetching && page === 1)),
    isFetchingMore: !isStale && isFetching && page > 1,
    isError,
    refetch,
  };
};

export default usePrintCatalog;
